import { Grid, Paper, Chip, MenuItem } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  ArrowDropDown,
  Assignment as AssignmentIcon,
  CheckCircle,
  CheckRounded,
  DateRange,
  Description as DescriptionIcon,
  Event as EventIcon,
  ExpandMoreRounded,
  Group,
  GroupRounded,
  HomeWork as HomeWorkIcon,
  Loop,
  MenuBookRounded,
  Person as PersonIcon,
  SchoolRounded as SchoolIcon,
  VpnKey as VpnKeyIcon,
  Work as WorkIcon,
} from '@material-ui/icons';
import { addCfaToTraining } from '@webapps/business';
import { Button, FormikAutocomplete, FormikAutocompletePx, StyledSelectIcon, useAuth, useQuery, useMutation, useToast } from '@webapps/reactjs-tech-components';
import { useStyles } from '@webapps/styles';
import { ROUTES_PATH } from 'config/constants';
import PropTypes from 'prop-types';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { css } from '@emotion/css';
import { pluralize } from '@webapps/utils';
import { useEffect, useState } from 'react';
import ClockOff from 'components/Icons/ClockOff';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
const TrainingCard = ({ cfas, item, refetch, isLoading = false }) => {
  const { classes, global, cx, theme: { spacing } } = useStyles(styles);
  const { idUser } = useAuth();
  const { push } = useHistory();

  if (isLoading) {
    return (
      <div style={{ padding: spacing(2.5), borderRadius: 16, backgroundColor: '#FFF', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Titre */}
        <Skeleton
          variant="text"
          width="85%"
          height={28}
          className={classes.skeletonColor}
        />

        {/* Section infos avec icônes */}
        <div>
          {/* Diplôme */}
          <div className={classes.cardItem}>
            <SchoolIcon className={global.infoIcon} />
            <Skeleton
              variant="text"
              width="60%"
              height={20}
              className={classes.skeletonColor}
            />
          </div>

          {/* Type de formation */}
          <div className={classes.cardItem}>
            <DescriptionIcon className={global.infoIcon} />
            <Skeleton
              variant="text"
              width="45%"
              height={20}
              className={classes.skeletonColor}
            />
          </div>

          {/* Nombre de classes */}
          <div className={classes.cardItem}>
            <GroupRounded className={global.infoIcon} />
            <Skeleton
              variant="text"
              width="70%"
              height={20}
              className={classes.skeletonColor}
            />
          </div>
        </div>

        {/* Dropdown CFA */}
        <div style={{ position: 'relative' }}>
          <Skeleton
            variant="rect"
            width="100%"
            height={40}
            style={{ borderRadius: 10 }}
            className={classes.skeletonColor}
          />
          <ExpandMoreRounded
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#ccc'
            }}
          />
        </div>

        {/* Boutons côte à côte */}
        <div className={classes.doubleButtons}>
          <Skeleton
            variant="rect"
            width="100%"
            height={36}
            style={{ borderRadius: 4 }}
            className={classes.skeletonColor}
          />
          <Skeleton
            variant="rect"
            width="100%"
            height={36}
            style={{ borderRadius: 4 }}
            className={classes.skeletonColor}
          />
        </div>
      </div>
    );
  }

  const { addToast } = useToast();

  const {
    isSuccess,
    error,
    mutateAsync: mutateAddCfaToTraining,
  } = useMutation(addCfaToTraining)

  const formik = useFormik({
    initialValues: {
      cfa: {
        id: item?.cfa?.id || '',
      }
    },
    onSubmit: (values) => {
      mutateAddCfaToTraining({
        id: item?.id,
        cfaId: values.cfa.id,
      });
    },
  });

  useEffect(() => {
    if (isSuccess) {
      addToast({
        status: 'success',
        message: 'CFA ajouté à la formation avec succès',
      });
      refetch();
    } else if (error) {
      addToast({
        status: 'error',
        message: 'Une erreur est survenue lors de l\'ajout du CFA à la formation',
      });
    }
  }, [isSuccess, error]);

  return (
    <div style={{ padding: spacing(2.5), borderRadius: 16, backgroundColor: '#FFF', display: 'flex', flexDirection: 'column', gap: 16 }} >
      <div className={cx(global.title, global.ellipsis90)}> {item?.label} </div>
      <div>
        <div className={classes.cardItem}>
          <SchoolIcon className={global.infoIcon} />
          <div className={global.infoText}>{item?.degree?.label}</div>
        </div>
        <div className={classes.cardItem}>
          <DescriptionIcon className={global.infoIcon} />
          <div className={global.infoText}>{item?.trainingType?.label}</div>
        </div>
        <div className={classes.cardItem}>
          <GroupRounded className={global.infoIcon} />
          <div className={global.infoText}>
            <span>{item?.classroomsCount} {pluralize('classe', item?.classroomsCount)} {pluralize('liée', item?.classroomsCount)} à la formation</span>
          </div>
        </div>
      </div>
      <div className={global.formikAutocomplete}>
        <FormikAutocompletePx
          formik={formik}
          fullWidth
          getOptionLabel={(option) => option.name || ''}
          onChange={(event, value) => {
            formik.setFieldValue('cfa.id', value?.id);
            formik.handleSubmit();
          }}
          name="cfa"
          borderRadius={10}
          select
          disableClearable
          options={cfas}
          value={cfas.find((option) => option?.id === formik.values.cfa.id) || null}
          endAdornment={<ExpandMoreRounded />}
          startAdornment={
            cfas.find((option) => option?.id === formik.values.cfa.id) ?
              <div className={classes["framerDiv"]}>
                <CheckRounded className={classes["framerIcon1"]} />
              </div> :
              <ClockOff className={css({ marginLeft: 4, color: "#5D75A8" })} />
          }
        />
      </div>

      <div className={classes.doubleButtons}>
        <Button onClick={() => push(ROUTES_PATH.SCHOOL_FORMATIONS_CANDIDATS, { trainingLabel: item?.label })} className={global.greyButton} variant="outlined" fullWidth> Voir la formation </Button>
        <Button onClick={() => push(ROUTES_PATH.SCHOOL_CLASSES, { trainingId: item?.id })} className={global.greyButton} variant="outlined" fullWidth> Voir les classes </Button>
      </div>
    </div>
  );
};

const styles = ({ spacing }) => ({
  paper: {
    padding: spacing(2.5),
    borderRadius: 16,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
  },
  cardItem: {
    display: 'flex',
    marginTop: spacing(0.5),
    alignItems: 'center',
  },
  cardItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
  },
  doubleButtons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
  },
  itemText: {
    color: "#5D75A8",
  },
  buttonHover: {
    "&:hover": {
      backgroundColor: "#0060F9",
      color: "white!important",
    },
  },
  framerDiv: {
    border: "1px dashed #008000",
    borderRadius: 12,
    backgroundColor: "#dbfad8",
    display: "flex",
    flexDirection: "row",
    alignContent: "center",
    height: "min-content",
    padding: "3px",
    position: "relative",
    width: "min-content",
    zIndex: "8",
    gap: "30px",
    overflow: "hidden",
    placeItems: "center",
    marginLeft: 6,
  },
  framerIcon1: {
    height: "16px",
    position: "relative",
    width: "16px",
    fill: "#008000",
  },
  skeletonColor: {
    backgroundColor: '#D6E7FF !important',
    '&::after': {
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
    },
  },
});

export default TrainingCard;
